/**
 * occurrenceZone — the zone a chosen hour travels with, and what a writer does when the server
 * cannot resolve the one this browser reported.
 *
 * ── AN HOUR TRAVELS WITH THE CLOCK ITS DIGITS CAME OFF ────────────────────────────────────────
 * Every write that carries a chosen hour now sends the IANA zone that hour was read in, and the
 * server canonicalises it and FREEZES it on the row (ADR 2026-09-08, "an occurrence is a wall time
 * in a zone and its instant is derived"). A trainer booking 17:00 while abroad books 17:00 where
 * they are standing, and editing their profile zone later moves nothing already booked.
 *
 * ── ABSENT IS AN ANSWER; A DEFAULT IS NOT ─────────────────────────────────────────────────────
 * {@link browserTimeZone} answers `null` rather than guessing. A default would be stamped onto the
 * row and frozen there, permanently and invisibly. Saying nothing instead reaches the server's own
 * resolver chain — the trainer's profile zone, then the client's, then the business default — which
 * is exactly the semantics every request had before this change.
 *
 * ── THE ONE RETRY ─────────────────────────────────────────────────────────────────────────────
 * `unknown_time_zone` is a hard refusal of an id this server cannot canonicalise: a browser whose
 * tzdata names a zone the server's release has not got yet, or one reporting something that is not
 * an IANA id at all. Before this change those browsers sent no zone and booked perfectly well, so a
 * write refused for the zone ALONE is sent once more with every `zoneId` dropped. One retry, on one
 * code, and only where the body actually named a zone. Any other refusal is a verdict about the
 * booking itself and is handed straight back — retrying it would make a refused write look like a
 * flaky one.
 *
 * The strip is recursive because `zoneId` sits at two depths on the wire: top level on reschedule,
 * promote and assign, and (on the shapes this app does not yet write) once per member of a
 * recurring body. One rule — "the same body with no zone anywhere" — covers both and cannot rot
 * when a third shape arrives.
 *
 * Mirrors `move-with-me-frontend/.../shared/lib/occurrenceZone.ts` in rule, not in code: the two
 * apps read the same refusal off different error shapes.
 *
 * No React, no DOM beyond `Intl`, no query hook, no i18next.
 */

import { ApiError } from '~/shared/api/mutator';

/** The backend's code for a zone id it could not canonicalise (`ValidationErrorCodes.UnknownTimeZone`). */
const UNKNOWN_TIME_ZONE_CODE = 'unknown_time_zone';

const ZONE_FIELD = 'zoneId';

/**
 * The IANA zone this browser stands in, or `null` when it does not report a resolvable one.
 *
 * Resolvability is checked by asking `Intl` to build a formatter in it, which is the same question
 * the server asks of its own tz database and the only one that can be asked here: a browser can
 * report a fixed offset (`GMT+02:00`) or an id this runtime knows nothing about, and both are the
 * cases that would otherwise reach the server as `unknown_time_zone`.
 */
export function browserTimeZone(): string | null {
  return resolvableZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
}

/**
 * `id` when this browser's `Intl` can format in it, else `null` — the one probe for "is this a
 * zone we can draw in". The booking calendar asks it of the trainer's published zone, which may be
 * absent or name something this runtime's tz data has not got.
 */
export function resolvableZone(id: string | null | undefined): string | null {
  if (!id) return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: id });
    return id;
  } catch {
    return null;
  }
}

/** A body with every `zoneId` removed, and whether there was one to remove. */
interface Stripped<T> {
  readonly body: T;
  readonly hadZone: boolean;
}

/**
 * The same value with every `zoneId` property removed, at any depth, and whether the walk found one.
 *
 * One walk answers both questions the retry asks — what to send next, and whether there is anything
 * different to send at all — because they are the same traversal. Two walks would be two chances to
 * disagree about what counts as a zone.
 *
 * Arrays and plain objects only: a `Date`, a `Map` or any other class instance is DATA here, not a
 * shape to walk, and rebuilding one from its enumerable keys would quietly turn it into a plain
 * object.
 */
export function withoutZoneId<T>(value: T): Stripped<T> {
  if (Array.isArray(value)) {
    // `as unknown[]`: `Array.isArray` narrows a generic to `any[]`, and mapping over `any` would put
    // an unchecked value back into `T`. `unknown` is the honest type of a member of a body this
    // function knows nothing about.
    const items = (value as unknown[]).map((item) => withoutZoneId(item));
    return {
      body: items.map((item) => item.body) as unknown as T,
      hadZone: items.some((item) => item.hadZone),
    };
  }
  if (value === null || typeof value !== 'object') return { body: value, hadZone: false };
  if (Object.getPrototypeOf(value) !== Object.prototype) return { body: value, hadZone: false };

  const stripped: Record<string, unknown> = {};
  let hadZone = false;
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (key === ZONE_FIELD) {
      hadZone = true;
      continue;
    }
    const walked = withoutZoneId(item);
    stripped[key] = walked.body;
    hadZone = hadZone || walked.hadZone;
  }
  return { body: stripped as T, hadZone };
}

/** True when this failure is the server saying it could not resolve the zone the body named. */
export function isUnknownTimeZone(error: unknown): boolean {
  return error instanceof ApiError && error.code === UNKNOWN_TIME_ZONE_CODE;
}

/**
 * Send `body`; if the server refuses it for the zone alone, send it once more with no zone at all.
 *
 * @param send the one call this write makes. Invoked at most twice, with the SAME body both times
 *   bar the zone, so nothing else about the request can differ between the two attempts.
 */
export async function sendRetryingWithoutZone<TBody, TResult>(
  body: TBody,
  send: (body: TBody) => Promise<TResult>,
): Promise<TResult> {
  try {
    return await send(body);
  } catch (error) {
    if (!isUnknownTimeZone(error)) throw error;
    const stripped = withoutZoneId(body);
    // Nothing to drop means nothing to retry: a body that named no zone would go a second time
    // byte-identical, and the server has already answered it. The refusal then came from a zone
    // resolved server-side, which no request of ours can change.
    if (!stripped.hadZone) throw error;
    return send(stripped.body);
  }
}
